"use client";

import { ArrowRight, Mail, Phone, Upload, User, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Link } from "@/components/common/Link";
const trustMarkers = ["Neutral, trauma-informed approach", "Court-compliant documentation", "Child-centered environment"];
const projectTypes = ["Supervised Parenting Time", "Monitored Exchange", "Transportation Services", "Parenting Education", "Other"];
function InquiryFormFields() {
  const {
    control,
    handleSubmit,
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    resetForm
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="border border-secondary p-8 bg-card">
        <p className="font-mono text-xs uppercase tracking-widest text-primary mb-3">— Inquiry Received</p>
        <p className="font-serif text-lg text-foreground mb-6">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="font-mono text-xs uppercase tracking-widest">
          Submit Another Inquiry
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <FormField control={control} name="name" render={({
      field
    }) => <FormItem data-index={0} className="border-b border-border pb-2">
            <FormLabel className="font-mono text-[0.65rem] uppercase tracking-[0.2em] text-muted-foreground">
              Your Full Name *
            </FormLabel>
            <FormControl>
              <Input {...field} placeholder="Enter your name" className="border-0 bg-transparent px-0 focus-visible:ring-0 focus-visible:border-0 text-foreground font-sans text-base h-10" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" render={({
      field
    }) => <FormItem data-index={1} className="border-b border-border pb-2">
            <FormLabel className="font-mono text-[0.65rem] uppercase tracking-[0.2em] text-muted-foreground">
              Email Address *
            </FormLabel>
            <FormControl>
              <Input {...field} type="email" placeholder="email@example.com" className="border-0 bg-transparent px-0 focus-visible:ring-0 focus-visible:border-0 text-foreground font-sans text-base h-10" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="phone" render={({
      field
    }) => <FormItem data-index={2} className="border-b border-border pb-2">
            <FormLabel className="font-mono text-[0.65rem] uppercase tracking-[0.2em] text-muted-foreground">
              Phone Number *
            </FormLabel>
            <FormControl>
              <Input {...field} type="tel" placeholder="(555) 000-0000" className="border-0 bg-transparent px-0 focus-visible:ring-0 focus-visible:border-0 text-foreground font-sans text-base h-10" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="service_type" render={({
      field
    }) => <FormItem data-index={3} className="border-b border-border pb-2">
            <FormLabel className="font-mono text-[0.65rem] uppercase tracking-[0.2em] text-muted-foreground">
              Service Requested *
            </FormLabel>
            <FormControl>
              <Select onValueChange={field.onChange} value={field.value}>
                <SelectTrigger className="border-0 bg-transparent px-0 focus-visible:ring-0 focus-visible:border-0 text-foreground font-sans text-base h-10">
                  <SelectValue placeholder="Select a service" />
                </SelectTrigger>
                <SelectContent className="bg-popover border-border">
                  {projectTypes.map((type, i) => <SelectItem key={type} data-index={i} value={type} className="font-sans">
                      {type}
                    </SelectItem>)}
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="details" render={({
      field
    }) => <FormItem data-index={4} className="border-b border-border pb-2">
            <FormLabel className="font-mono text-[0.65rem] uppercase tracking-[0.2em] text-muted-foreground">
              Case Details *
            </FormLabel>
            <FormControl>
              <Textarea {...field} rows={6} placeholder="Please provide brief context regarding your needs." className="border-0 bg-transparent px-0 focus-visible:ring-0 focus-visible:border-0 text-foreground font-sans text-base resize-none" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <div className="flex items-start gap-3 pt-4">
        <Checkbox id="privacy-agree" className="mt-1" />
        <label htmlFor="privacy-agree" className="font-sans text-sm text-foreground leading-relaxed cursor-pointer">
          I understand that Tiny to Teen Supervised Parenting & Education Services provides neutral, professional support and I consent to be contacted regarding my inquiry.
        </label>
      </div>

      {submitError && <div className="border border-destructive p-4 bg-destructive/10">
          <p className="font-mono text-xs text-destructive">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full bg-primary text-white hover:bg-primary/90 font-default font-bold uppercase tracking-widest text-sm py-6 shadow-md transition-shadow">
        {isSubmitting ? "Submitting..." : "Submit Intake Request"}
        <ArrowRight className="size-4 ml-2" />
      </Button>
    </Form>;
}
export default function Intake() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <section data-section-id="4171" id="begin-intake" className="bg-background text-foreground py-20 lg:py-32 px-6 lg:px-12 relative overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div ref={leftRef} className={`lg:col-span-5 transition-all duration-1000 ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-primary mb-8">
              § 01 — Intake
            </p>

            <h2 className="font-default font-bold text-foreground leading-[0.95] mb-8 uppercase" style={{
            fontSize: "clamp(2.5rem, 5vw, 5rem)",
            letterSpacing: "-0.03em"
          }}>
              Begin your
              <br />
              intake process.
            </h2>

            <p className="font-sans text-lg text-muted-foreground leading-relaxed mb-12 max-w-md">
              We provide a structured, neutral, and trauma-informed path for families navigating court-involved matters. Please complete the form to initiate your intake request.
            </p>

            <ul className="space-y-4 mb-12">
              {trustMarkers.map((marker, i) => <li key={marker} data-index={i} className="flex items-center gap-4">
                  <span className="inline-block size-3 bg-primary shrink-0" aria-hidden="true" />
                  <span className="font-mono text-sm uppercase tracking-wider text-foreground">
                    {marker}
                  </span>
                </li>)}
            </ul>

            <div className="backdrop-blur-sm bg-card/40 border border-border p-6 shadow-md">
              <p className="font-mono text-[0.65rem] uppercase tracking-[0.3em] text-primary mb-4">
                Direct Contact — Arizona
              </p>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <User className="size-4 text-muted-foreground shrink-0" />
                  <span className="font-mono text-sm text-foreground">Intake Coordinator</span>
                </div>
                <div className="flex items-center gap-3">
                  <Mail className="size-4 text-muted-foreground shrink-0" />
                  <Link to="mailto:info@tinytoteenservices.org" className="font-mono text-sm text-foreground hover:text-primary transition-colors">info@tinytoteenservices.org</Link>
                </div>
                <div className="flex items-center gap-3">
                  <Phone className="size-4 text-muted-foreground shrink-0" />
                  <Link to="tel:+16025550123" className="font-mono text-sm text-foreground hover:text-primary transition-colors">(602)904-9072</Link>
                </div>
              </div>
            </div>
          </div>

          <div ref={rightRef} className={`lg:col-span-7 transition-all duration-1000 delay-150 ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="bg-card border border-border p-8 lg:p-12 shadow-md">
              <FormProvider formKey="intake_form_intake_path" formId="intake_form_intake_path" sectionName="begin-intake" defaultValues={{
              name: "",
              email: "",
              phone: "",
              service_type: "",
              details: ""
            }} validationRules={{
              name: {
                required: true
              },
              email: {
                required: true,
                pattern: "email"
              },
              phone: {
                required: true,
                pattern: "phone"
              },
              service_type: {
                required: true
              },
              details: {
                required: true
              }
            }} submitText="Submit Intake Request" successMessage="Thank you. Your intake request has been received. A member of our team will contact you shortly to discuss next steps." errorMessage="Submission failed. Please try again or contact us directly.">
                <div className="mb-8 p-4 bg-accent/30 border-l-4 border-primary flex gap-4 items-start">
                  <Info className="size-5 text-primary shrink-0 mt-0.5" />
                  <div className="space-y-2">
                    <p className="font-mono text-[0.7rem] uppercase tracking-widest text-primary font-bold">
                      Payment Policy Notice
                    </p>
                    <p className="font-sans text-sm text-foreground leading-relaxed">
                      All services require pre-payment and are non-refundable. Tiny to Teen Supervised Parenting & Education Services maintains firm boundaries regarding fee schedules to ensure service neutrality. Please contact our intake coordinator for the full payment policy and current rates.
                    </p>
                  </div>
                </div>
                <InquiryFormFields />
              </FormProvider>
            </div>
          </div>
        </div>
      </div>
    </section>;
}